import { useCallback, useEffect, useReducer, useRef } from 'react'
import { fetchSSORoles, refreshSSOAccessToken, UnauthorizedError } from '../../api/authorizationApi'
import { ALLOWED_ACCOUNT_IDS } from '../../config/accounts'
import { ROLE_NAMES } from '../../config/roles'
import type { Role } from '../../types/Role'
import { useDeviceCodeFlow, type DeviceCodeFlowState } from './useDeviceCodeFlow'

const REFETCH_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes, matches cdh-data-portal-internal's roles.ts cadence

// ponytail: local/testing-only bypass so the app shell can be exercised without a live AWS SSO
// device-flow/Authorization API session -- mirrors useAuthStatus.ts's VITE_BYPASS_AUTH flag (same
// flag, since both gates need to be skipped together to reach any page locally). Unset/false by
// default; only active if a developer deliberately enables it in their own untracked .env file.
const isAuthBypassEnabled = import.meta.env.VITE_BYPASS_AUTH === 'true'

const bypassDeviceFlowState: DeviceCodeFlowState = {
  accessToken: 'local-bypass-token',
  error: null,
  secondsRemaining: null,
  status: 'success',
  userCode: null,
  verificationUri: null,
}

const bypassRole: Role = {
  roleArn: `arn:aws:iam::${ALLOWED_ACCOUNT_IDS.dev}:role/${ROLE_NAMES.devOps}`,
  principalArn: '',
  accountId: ALLOWED_ACCOUNT_IDS.dev,
  environment: 'dev',
  friendlyName: ROLE_NAMES.devOps,
}

interface RolesState {
  readonly roles: Role[]
  readonly rolesError: Error | null
  readonly rolesLoading: boolean
  readonly rolesLoaded: boolean
}

type RolesAction =
  | { type: 'ROLES_FETCH_FAILED'; error: Error }
  | { type: 'ROLES_FETCH_STARTED' }
  | { type: 'ROLES_FETCH_SUCCEEDED'; roles: Role[] }
  | { type: 'ROLES_RESET' }

const initialRolesState: RolesState = { roles: [], rolesError: null, rolesLoading: false, rolesLoaded: false }

function rolesReducer(state: RolesState, action: RolesAction): RolesState {
  switch (action.type) {
    case 'ROLES_FETCH_STARTED':
      return { ...state, rolesError: null, rolesLoading: true }
    case 'ROLES_FETCH_SUCCEEDED':
      return { roles: action.roles, rolesError: null, rolesLoading: false, rolesLoaded: true }
    case 'ROLES_FETCH_FAILED':
      return { ...state, rolesError: action.error, rolesLoading: false }
    case 'ROLES_RESET':
      return initialRolesState
    default:
      return state
  }
}

export interface AuthorizationHookResult extends RolesState {
  /**
   * Checks whether the current user has a role whose friendly name matches the given pattern
   * (case-insensitive). This is UX-only gating -- see components/authorization/RequireRole.tsx
   * for why it must never be treated as the security boundary.
   */
  hasRole: (friendlyName: string) => boolean
  /**
   * True only for the CDHX-DevOps role. CDHX-ReadOnly and CDHX-Support are both read-only for
   * now -- see components/authorization/RequireWriteAccess.tsx for the corresponding UI gate.
   */
  hasWriteAccess: boolean
}

/** Publicly-relevant device flow state -- the access token itself stays internal to this hook. */
export type PublicDeviceCodeFlowState = Omit<DeviceCodeFlowState, 'accessToken'> & { startFlow: () => void }

export interface UseAuthorizationResult {
  authorization: AuthorizationHookResult
  /**
   * Drives the AWS SSO device flow dialog (components/pages/DeviceFlowLogin). App.tsx should
   * block the app shell on anything other than `status === 'success'`.
   */
  deviceFlow: PublicDeviceCodeFlowState
}

/**
 * Fetches the current user's AWS SSO roles once they've completed the device authorization
 * flow, then periodically refreshes them. Pass `enabled: false` while the user isn't signed in
 * yet (mirrors useAuthStatus's status gate). On enable, it first tries to silently resume the
 * AWS SSO session via the Authorization API's own session cookie (refreshSSOAccessToken) --
 * only falling back to the device-code approval UI if that fails -- and can be started again
 * (e.g. after sign out/in) since it's re-armed whenever `enabled` toggles back on.
 */
export function useAuthorization(enabled: boolean): UseAuthorizationResult {
  const deviceCodeFlow = useDeviceCodeFlow()
  const [rolesState, dispatch] = useReducer(rolesReducer, initialRolesState)
  const accessTokenRef = useRef<string | null>(null)
  accessTokenRef.current = deviceCodeFlow.accessToken
  // Set right before updateAccessToken() is called from within an in-flight 401 retry (below),
  // so the roles-fetch effect -- which depends on deviceCodeFlow.accessToken -- knows the
  // re-render it's about to see was caused by a refresh whose roles it already has, and skips
  // firing an immediate duplicate loadRoles() call for it.
  const skipNextLoadRolesRef = useRef(false)

  const startFlow = useCallback(() => {
    void deviceCodeFlow.startFlow()
  }, [deviceCodeFlow.startFlow])

  useEffect(() => {
    if (!enabled) {
      deviceCodeFlow.reset()
      dispatch({ type: 'ROLES_RESET' })
      return
    }

    if (isAuthBypassEnabled) {
      dispatch({ type: 'ROLES_FETCH_SUCCEEDED', roles: [bypassRole] })
      return
    }

    let cancelled = false

    // On mount, try to silently resume the AWS SSO session via the Authorization API's own
    // HttpOnly session cookie (see refreshSSOAccessToken) before falling back to the full
    // device-code approval UI. This avoids re-prompting the user on every page refresh without
    // ever persisting the bearer token itself in browser storage (localStorage/sessionStorage
    // would be readable by any script on the page, e.g. an XSS payload).
    refreshSSOAccessToken()
      .then((refreshed) => {
        if (!cancelled) {
          deviceCodeFlow.updateAccessToken(refreshed.access_token)
        }
      })
      .catch(() => {
        if (!cancelled) {
          startFlow()
        }
      })

    return () => {
      cancelled = true
    }
  }, [enabled, startFlow, deviceCodeFlow.reset, deviceCodeFlow.updateAccessToken])

  useEffect(() => {
    if (!enabled || isAuthBypassEnabled || !deviceCodeFlow.accessToken) {
      return
    }

    let cancelled = false

    // Retries once with a freshly refreshed access token on a 401, mirroring
    // cdh-data-portal-internal's useSSOApiCall.withSSOTokenRefresh().
    async function fetchRolesWithTokenRefresh(): Promise<Role[]> {
      const token = accessTokenRef.current

      if (!token) {
        throw new Error('No SSO access token')
      }

      try {
        return await fetchSSORoles(token)
      } catch (error) {
        if (!(error instanceof UnauthorizedError)) {
          throw error
        }

        const refreshed = await refreshSSOAccessToken()
        accessTokenRef.current = refreshed.access_token
        const roles = await fetchSSORoles(refreshed.access_token)
        skipNextLoadRolesRef.current = true
        deviceCodeFlow.updateAccessToken(refreshed.access_token)
        return roles
      }
    }

    function loadRoles() {
      dispatch({ type: 'ROLES_FETCH_STARTED' })

      fetchRolesWithTokenRefresh()
        .then((roles) => {
          if (!cancelled) {
            dispatch({ type: 'ROLES_FETCH_SUCCEEDED', roles })
          }
        })
        .catch((error: unknown) => {
          if (!cancelled) {
            dispatch({ type: 'ROLES_FETCH_FAILED', error: error instanceof Error ? error : new Error(String(error)) })
          }
        })
    }

    if (skipNextLoadRolesRef.current) {
      skipNextLoadRolesRef.current = false
    } else {
      loadRoles()
    }
    const intervalId = setInterval(loadRoles, REFETCH_INTERVAL_MS)

    return () => {
      cancelled = true
      clearInterval(intervalId)
    }
  }, [enabled, deviceCodeFlow.accessToken])

  const { accessToken: _accessToken, ...publicDeviceCodeFlowState } = deviceCodeFlow
  const { accessToken: _bypassAccessToken, ...publicBypassDeviceFlowState } = bypassDeviceFlowState

  return {
    authorization: {
      ...rolesState,
      hasRole: (friendlyName: string) =>
        rolesState.roles.some((role) => role.friendlyName.toLowerCase() === friendlyName.toLowerCase()),
      hasWriteAccess: rolesState.roles.some((role) => role.friendlyName === ROLE_NAMES.devOps),
    },
    deviceFlow: isAuthBypassEnabled
      ? { ...publicBypassDeviceFlowState, startFlow }
      : { ...publicDeviceCodeFlowState, startFlow },
  }
}
